import { query, mutation, fetchApi } from "./convex";
import { isMockEnabled } from "../config";
import type {
  ProfilePreviewResponse,
  ReliabilityStatus,
  MonitoredProfile,
  MonitoringEvent,
  DashboardStats,
  ActivityHours,
} from "../domain/types";
import { normalizeInstagramHandle, isValidInstagramHandle } from "@lib/utils";

export async function lookupProfile(
  handle: string,
): Promise<ProfilePreviewResponse> {
  const normalized = normalizeInstagramHandle(handle);

  if (!isValidInstagramHandle(normalized)) {
    return {
      availability: "not_found",
      reliabilityStatus: "unknown",
      message: "Invalid Instagram handle format.",
    };
  }

  if (isMockEnabled()) return mockLookupProfile(normalized);

  try {
    // Use obfuscated HTTP endpoint via fetchApi utility
    const result = await fetchApi("/api/v1/preview", {
      username: normalized,
      previewSessionId: getPreviewSessionId(),
    });
    return mapConvexResultToPreview(result, normalized);
  } catch (error) {
    return mapLookupErrorToPreview(error, normalized);
  }
}

export function mapLookupErrorToPreview(
  error: unknown,
  handle: string,
): ProfilePreviewResponse {
  const payload = extractConvexErrorPayload(error);
  const code = payload?.code;
  const friendlyMessage = payload?.friendlyMessage;
  const message =
    payload?.message ??
    (error instanceof Error ? error.message : "Unknown error");
  const normalizedMessage = message.toLowerCase();

  if (code === "USER_NOT_FOUND" || code === "INVALID_HANDLE") {
    return {
      availability: "not_found",
      reliabilityStatus: "unknown",
      message: friendlyMessage ?? "Profile not found.",
    };
  }

  if (
    code === "PRIVATE_PROFILE" ||
    code === "PRIVATE_ACCESS_DENIED" ||
    normalizedMessage.includes("private")
  ) {
    return {
      availability: "private_not_available",
      reliabilityStatus: "private_profile",
      profile: { handle, isPrivate: true },
      message: friendlyMessage ?? "This profile is private.",
    };
  }

  if (
    code === "API_TIMEOUT" ||
    code === "NETWORK_LOST" ||
    normalizedMessage.includes("timeout") ||
    normalizedMessage.includes("network")
  ) {
    return {
      availability: "provider_error",
      reliabilityStatus: "provider_error",
      message:
        friendlyMessage ??
        "We could not reach the data provider. Please try again.",
    };
  }

  if (
    normalizedMessage.includes("429") ||
    normalizedMessage.includes("rate") ||
    friendlyMessage?.toLowerCase().includes("rate")
  ) {
    return {
      availability: "rate_limited",
      reliabilityStatus: "provider_error",
      message: friendlyMessage ?? "Rate limited. Try again later.",
    };
  }

  return {
    availability: "provider_error",
    reliabilityStatus: "provider_error",
    message: friendlyMessage ?? `Unable to fetch: ${message}`,
  };
}

function extractConvexErrorPayload(error: unknown): {
  code?: string;
  message?: string;
  friendlyMessage?: string;
} | null {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data?: unknown }).data;
    if (data && typeof data === "object") {
      return data as {
        code?: string;
        message?: string;
        friendlyMessage?: string;
      };
    }
  }

  const raw = error instanceof Error ? error.message : String(error ?? "");
  const jsonStart = raw.indexOf("{");
  const jsonEnd = raw.lastIndexOf("}");
  if (jsonStart === -1 || jsonEnd <= jsonStart) return null;

  try {
    const parsed = JSON.parse(raw.slice(jsonStart, jsonEnd + 1));
    if (parsed && typeof parsed === "object") {
      return parsed as {
        code?: string;
        message?: string;
        friendlyMessage?: string;
      };
    }
  } catch {
    return null;
  }

  return null;
}

export async function getMonitoredProfiles(
  _userId: string,
): Promise<MonitoredProfile[]> {
  if (isMockEnabled()) return mockMonitoredProfiles;
  try {
    const result = await query("monitors:list", {});
    return mapMonitorsResult(result);
  } catch {
    return [];
  }
}

export async function getProfileEvents(
  monitoredAccountId: string,
  limit = 20,
): Promise<MonitoringEvent[]> {
  if (isMockEnabled()) return mockEvents.slice(0, limit);
  try {
    const result = await query("events:getRecentEvents", {
      monitoredAccountId,
      limit,
    });
    return mapEventsResult(result);
  } catch {
    return [];
  }
}

export async function startMonitoring(
  handle: string,
  _userId: string,
): Promise<{ success: boolean; error?: string }> {
  const normalized = normalizeInstagramHandle(handle);
  if (isMockEnabled()) return { success: true };
  try {
    await mutation("monitors:add", { handle: normalized });
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Failed to start monitoring",
    };
  }
}

export async function stopMonitoring(
  monitorId: string,
): Promise<{ success: boolean }> {
  if (isMockEnabled()) return { success: true };
  try {
    await mutation("monitors:remove", { monitorId });
    return { success: true };
  } catch {
    return { success: false };
  }
}

export async function getDashboardStats(): Promise<DashboardStats> {
  if (isMockEnabled()) {
    return {
      monitoredCount: mockMonitoredProfiles.length,
      activeCount: mockMonitoredProfiles.filter((p) => p.status === "ACTIVE")
        .length,
      pausedCount: 0,
      newFollowingCount: 1,
      unfollowsCount: 1,
      newFollowersCount: 0,
    };
  }

  try {
    const result = await query("stats:dashboardStats", {});
    return mapDashboardStats(result);
  } catch {
    return mapDashboardStats(null);
  }
}

export async function getProfileActivityHours(
  monitoredAccountId: string,
): Promise<ActivityHours | null> {
  if (isMockEnabled()) {
    return {
      blocks: [
        { label: "0-4", count: 0 },
        { label: "4-8", count: 1 },
        { label: "8-12", count: 2 },
        { label: "12-16", count: 1 },
        { label: "16-20", count: 3 },
        { label: "20-24", count: 2 },
      ],
      totalWithHour: 9,
    };
  }

  try {
    const result = await query("engagement:getActivityHours", {
      monitoredAccountId,
    });
    return mapActivityHours(result);
  } catch {
    return null;
  }
}

// ============================================================================
// Mappers
// ============================================================================

function mapConvexResultToPreview(
  data: unknown,
  handle: string,
): ProfilePreviewResponse {
  const d = data as Record<string, unknown> | null;
  if (!d)
    return {
      availability: "not_found",
      reliabilityStatus: "unknown",
      message: "No data available.",
    };

  if (d.isPrivate === true || d.is_private === true) {
    return {
      availability: "private_not_available",
      reliabilityStatus: "private_profile",
      profile: {
        handle,
        fullName: (d.fullName ?? d.full_name) as string | undefined,
        isPrivate: true,
      },
      message: "This profile is private.",
    };
  }

  const previewFollowing = Array.isArray(
    d.previewFollowing ?? d.preview_following ?? d.recentFollowings,
  )
    ? ((d.previewFollowing ??
        d.preview_following ??
        d.recentFollowings) as Record<string, unknown>[])
    : [];

  return {
    availability: "public_available",
    profile: {
      handle,
      fullName: (d.fullName ?? d.full_name) as string | undefined,
      profilePicUrl: (d.profilePicUrl ?? d.profile_pic_url) as
        string | undefined,
      isVerified: (d.isVerified ?? d.is_verified) as boolean | undefined,
      isPrivate: false,
      followersCount: (d.followersCount ?? d.followers_count) as
        number | undefined,
      followingCount: (d.followingCount ?? d.following_count) as
        number | undefined,
    },
    reliabilityStatus: inferReliability(d),
    previewFollowing: previewFollowing.map(mapPreviewFollowing),
    lastCheckedAt: d.lastCheckedAt
      ? new Date(d.lastCheckedAt as number).toISOString()
      : new Date().toISOString(),
  };
}

export function getPreviewSessionId(): string {
  const key = "stalkie_preview_session_id";
  try {
    const stored = localStorage.getItem(key);
    if (stored) return stored;
    const generated = crypto.randomUUID();
    localStorage.setItem(key, generated);
    return generated;
  } catch {
    return `web-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

function inferReliability(d: Record<string, unknown>): ReliabilityStatus {
  const v = d.providerReliabilityVerdict as string | undefined;
  if (v === "RELIABLE") return "reliable";
  if (v === "PROBABLY_RELIABLE") return "probably_reliable";
  if (v === "UNSTABLE") return "degraded";
  if (v === "UNUSABLE_FOR_DIFF" || v === "INCONCLUSIVE")
    return "incomplete_data";
  if (d.error || d.errorMessage) return "provider_error";
  return "probably_reliable";
}

function mapPreviewFollowing(f: Record<string, unknown>) {
  return {
    handle: (f.handle ?? f.username ?? "") as string,
    fullName: f.fullName as string | undefined,
    profilePicUrl: f.profilePicUrl as string | undefined,
    isVerified: f.isVerified as boolean | undefined,
    isPrivate: f.isPrivate as boolean | undefined,
    gender: (f.gender ?? "unknown") as "male" | "female" | "unknown",
    genderSource: (f.genderSource ?? "UNKNOWN") as
      "LLM" | "HEURISTIC" | "UNKNOWN",
    confidence: f.confidence as string | undefined,
  };
}

export function mapMonitorsResult(result: unknown): MonitoredProfile[] {
  if (!Array.isArray(result)) return [];
  return result.map((m: Record<string, unknown>) => {
    const account = (
      m.account && typeof m.account === "object" ? m.account : {}
    ) as Record<string, unknown>;
    const userVisibleStatus = (m.userVisibleStatus ??
      account.status ??
      "ACTIVE") as string;
    return {
      id: (m._id ?? m.id ?? "") as string,
      monitoredAccountId: (m.monitoredAccountId ?? "") as string,
      handle: (account.handle ?? m.handle ?? "") as string,
      displayName: (m.displayName ?? m.fullName) as string | undefined,
      avatarUrl: (account.profilePicUrl ?? m.avatarUrl ?? m.profilePicUrl) as
        string | undefined,
      status: mapUserVisibleStatus(userVisibleStatus),
      monitoringMode: (m.monitoringMode ??
        "NORMAL") as MonitoredProfile["monitoringMode"],
      followingCount: (account.followingCount ??
        m.followingCount ??
        0) as number,
      followersCount: (account.followersCount ??
        m.followersCount ??
        0) as number,
      newFollowsCount: (account.newFollowsCount ?? 0) as number,
      unfollowsCount: (account.unfollowsCount ?? 0) as number,
      newFollowersCount: (account.newFollowersCount ?? 0) as number,
      lastScrapedAt: account.lastScrapeAt
        ? new Date(account.lastScrapeAt as number).toISOString()
        : undefined,
      nextScrapeAt: m.nextScrapeAt
        ? new Date(m.nextScrapeAt as number).toISOString()
        : undefined,
      activeSince: m._creationTime
        ? new Date(m._creationTime as number).toISOString()
        : undefined,
      isPrivate:
        userVisibleStatus === "PRIVATE" ||
        (account.isPrivate ?? false) === true,
      isActive: (m.isActive ?? userVisibleStatus === "ACTIVE") as boolean,
      accessMode: account.accessMode as string | undefined,
    };
  });
}

export function mapEventsResult(result: unknown): MonitoringEvent[] {
  if (!Array.isArray(result)) return [];
  return result.map((e: Record<string, unknown>) => {
    const profile = (
      e.profile && typeof e.profile === "object" ? e.profile : {}
    ) as Record<string, unknown>;
    const snapshot = (
      e.profileSnapshot && typeof e.profileSnapshot === "object"
        ? e.profileSnapshot
        : {}
    ) as Record<string, unknown>;
    return {
      id: (e._id ?? e.id ?? "") as string,
      action: (e.action ?? "STARTED_FOLLOWING") as MonitoringEvent["action"],
      occurredAt: e.detectedAt
        ? new Date(e.detectedAt as number).toISOString()
        : e._creationTime
          ? new Date(e._creationTime as number).toISOString()
          : new Date().toISOString(),
      firstSignalAt:
        typeof e.firstSignalAt === "number"
          ? new Date(e.firstSignalAt).toISOString()
          : undefined,
      firstObservedAt:
        typeof e.firstObservedAt === "number"
          ? new Date(e.firstObservedAt).toISOString()
          : undefined,
      confirmedAt:
        typeof e.confirmedAt === "number"
          ? new Date(e.confirmedAt).toISOString()
          : undefined,
      notifiedAt:
        typeof e.notifiedAt === "number"
          ? new Date(e.notifiedAt).toISOString()
          : undefined,
      targetHandle: (e.otherHandle ??
        e.targetHandle ??
        e.handle ??
        "") as string,
      targetDisplayName: (profile.fullName ?? e.targetDisplayName) as
        string | undefined,
      targetAvatarUrl: (profile.profilePicUrl ??
        snapshot.profilePicUrl ??
        e.targetAvatarUrl) as string | undefined,
      confidence: (e.confidence ?? "high") as MonitoringEvent["confidence"],
      enrichmentStatus: (e.enrichmentStatus ??
        "DONE") as MonitoringEvent["enrichmentStatus"],
      isLocked: (e.isLocked ?? false) as boolean,
      isMutual: e.isMutual as boolean | undefined,
      category: profile.category as MonitoringEvent["category"],
      followerCount: profile.followerCount as number | undefined,
      followingCount: profile.followingCount as number | undefined,
      biography: profile.biography as string | undefined,
      gender: (snapshot.gender ?? profile.gender ?? "unknown") as any,
      isVerified: (profile.isVerified ??
        snapshot.isVerified ??
        false) as boolean,
      isPrivate: (profile.isPrivate !== undefined
        ? profile.isPrivate
        : snapshot.isPrivate !== undefined
          ? snapshot.isPrivate
          : undefined) as boolean | undefined,
    };
  });
}

export function mapDashboardStats(result: unknown): DashboardStats {
  const r =
    result && typeof result === "object"
      ? (result as Record<string, unknown>)
      : {};
  return {
    monitoredCount: (r.monitoredCount ?? 0) as number,
    activeCount: (r.activeCount ?? 0) as number,
    pausedCount: (r.pausedCount ?? 0) as number,
    newFollowingCount: (r.newFollowingCount ?? 0) as number,
    unfollowsCount: (r.unfollowsCount ?? 0) as number,
    newFollowersCount: (r.newFollowersCount ?? 0) as number,
  };
}

export function mapActivityHours(result: unknown): ActivityHours | null {
  if (!result || typeof result !== "object") return null;
  const r = result as Record<string, unknown>;
  if (!Array.isArray(r.blocks)) return null;
  return {
    blocks: r.blocks.map((block) => {
      const b =
        block && typeof block === "object"
          ? (block as Record<string, unknown>)
          : {};
      return {
        label: (b.label ?? "") as string,
        count: (b.count ?? 0) as number,
      };
    }),
    totalWithHour: (r.totalWithHour ?? 0) as number,
  };
}

/** Make backend time-block labels explicit in the activity heatmap. */
export function formatActivityHourLabel(label: string): string {
  const match = /^(\d{1,2})-(\d{1,2})$/.exec(label.trim());
  if (!match) return label;
  return `${match[1].padStart(2, "0")}h - ${match[2].padStart(2, "0")}h`;
}

function mapUserVisibleStatus(status: string): MonitoredProfile["status"] {
  if (status === "PAUSED") return "PAUSED_AUTO";
  if (
    status === "ACTIVE" ||
    status === "RATE_LIMITED" ||
    status === "PRIVATE" ||
    status === "RESTRICTED" ||
    status === "BUSINESS"
  ) {
    return status;
  }
  return "ACTIVE";
}

// ============================================================================
// Mock data
// ============================================================================

function mockLookupProfile(handle: string): ProfilePreviewResponse {
  if (handle === "private")
    return {
      availability: "private_not_available",
      reliabilityStatus: "private_profile",
      profile: { handle: "private", isPrivate: true },
      message: "This profile is private.",
    };
  if (handle === "notfound")
    return {
      availability: "not_found",
      reliabilityStatus: "unknown",
      message: "Profile not found.",
    };
  if (handle === "error")
    return {
      availability: "provider_error",
      reliabilityStatus: "provider_error",
      message: "Provider error.",
    };

  return {
    availability: "public_available",
    profile: {
      handle,
      fullName: "Example Profile",
      profilePicUrl: "",
      isVerified: false,
      isPrivate: false,
      followersCount: 1234,
      followingCount: 567,
    },
    reliabilityStatus: "reliable",
    previewFollowing: [
      {
        handle: "friend.one",
        fullName: "Friend One",
        isVerified: false,
        isPrivate: false,
        gender: "female",
        genderSource: "HEURISTIC",
      },
      {
        handle: "friend.two",
        fullName: "Friend Two",
        isVerified: true,
        isPrivate: false,
        gender: "male",
        genderSource: "LLM",
      },
      {
        handle: "friend.three",
        fullName: "Friend Three",
        isVerified: false,
        isPrivate: false,
        gender: "female",
        genderSource: "HEURISTIC",
      },
    ],
    lastCheckedAt: new Date().toISOString(),
  };
}

const mockMonitoredProfiles: MonitoredProfile[] = [
  {
    id: "1",
    monitoredAccountId: "account1",
    handle: "example.profile",
    displayName: "Example Profile",
    status: "ACTIVE",
    monitoringMode: "NORMAL",
    followingCount: 567,
    followersCount: 1234,
    newFollowsCount: 1,
    unfollowsCount: 1,
    newFollowersCount: 0,
    lastScrapedAt: new Date().toISOString(),
    activeSince: new Date(Date.now() - 7 * 86400000).toISOString(),
    isPrivate: false,
    isActive: true,
  },
];

const mockEvents: MonitoringEvent[] = [
  {
    id: "e1",
    action: "STARTED_FOLLOWING",
    occurredAt: new Date(Date.now() - 3600000).toISOString(),
    targetHandle: "new.friend",
    targetDisplayName: "New Friend",
    confidence: "high",
    enrichmentStatus: "DONE",
  },
  {
    id: "e2",
    action: "STOPPED_FOLLOWING",
    occurredAt: new Date(Date.now() - 7200000).toISOString(),
    targetHandle: "old.acquaintance",
    confidence: "medium",
    enrichmentStatus: "DONE",
  },
];
